import { useEffect, useState } from "react";
import api, { apiError, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import { toast } from "sonner";
import { Plus, Trash2, Camera, Loader2, Pencil, X, Undo2, User } from "lucide-react";

const empty = { cliente: "", prodotto: "", note: "", foto_file_ids: [] };

export default function Resi({ embedded = false }) {
  const { text } = useSettings();
  const { user } = useAuth();
  const canManage = user.role === "admin" || user.is_admin || !!user.permissions_effective?.resi?.manage;
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);
  const [editId, setEditId] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/resi"); setItems(data); } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const uploadPhotos = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setForm((f) => ({ ...f, foto_file_ids: [...f.foto_file_ids, data.file_id] }));
      }
      toast.success("Foto caricate");
    } catch (err) { toast.error(apiError(err)); }
    setUploading(false);
  };

  const removePhoto = (id) => setForm((f) => ({ ...f, foto_file_ids: f.foto_file_ids.filter((x) => x !== id) }));

  const startEdit = (r) => {
    setEditId(r.id);
    setForm({ cliente: r.cliente || "", prodotto: r.prodotto || "", note: r.note || "", foto_file_ids: r.foto_file_ids || [] });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const cancelEdit = () => { setEditId(null); setForm(empty); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.cliente.trim()) { toast.error("Inserisci il nome del cliente"); return; }
    try {
      if (editId) { await api.put(`/resi/${editId}`, form); toast.success("Reso aggiornato"); }
      else { await api.post("/resi", form); toast.success("Reso registrato"); }
      cancelEdit(); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/resi/${id}`); toast.success("Eliminato"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const input = "w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100 outline-none focus:border-primary";

  return (
    <div className="animate-fade-up">
      {!embedded && (
        <>
          <h1 className="font-display font-black text-4xl text-slate-50 tracking-tight mb-1">{text("resi_title")}</h1>
          <p className="text-slate-400 mb-6">{text("resi_subtitle")}</p>
        </>
      )}

      {canManage && (
        <form onSubmit={submit} data-testid="reso-form" className="bg-slate-900/50 border border-slate-800 rounded-md p-5 mb-8 grid grid-cols-1 md:grid-cols-2 gap-3">
          <input data-testid="reso-cliente" placeholder="Nome cliente" value={form.cliente} onChange={(e) => setForm({ ...form, cliente: e.target.value })} className={input} />
          <input data-testid="reso-prodotto" placeholder="Prodotto reso (opzionale)" value={form.prodotto} onChange={(e) => setForm({ ...form, prodotto: e.target.value })} className={input} />
          <label data-testid="reso-foto-label" className={`${input} flex items-center gap-2 cursor-pointer ${form.foto_file_ids.length ? "text-emerald-400 border-emerald-500/40" : "text-slate-400"}`}>
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
            <span className="truncate">{form.foto_file_ids.length ? `${form.foto_file_ids.length} foto allegate ✓` : "Allega foto (una o più)"}</span>
            <input data-testid="reso-foto-input" type="file" accept="image/*" multiple className="hidden" onChange={(e) => uploadPhotos(e.target.files)} />
          </label>
          <textarea data-testid="reso-note" placeholder="Motivo / info reso (stato merce, quantità, cliente…)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2} className={`${input} md:col-span-2 resize-none`} />
          {form.foto_file_ids.length > 0 && (
            <div className="md:col-span-2 flex flex-wrap gap-2">
              {form.foto_file_ids.map((id) => (
                <div key={id} className="relative">
                  <img src={fileUrl(id)} alt="foto" className="w-16 h-16 rounded-md object-cover border border-slate-700" />
                  <button type="button" onClick={() => removePhoto(id)} className="absolute -top-1.5 -right-1.5 bg-red-500 rounded-full p-0.5 text-white"><X className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
          )}
          <button data-testid="reso-submit" type="submit" className="bg-primary text-slate-950 font-semibold rounded-md px-4 py-2 flex items-center justify-center gap-2 hover:opacity-90 transition-opacity">
            {editId ? <Pencil className="w-4 h-4" /> : <Plus className="w-4 h-4" />} {editId ? "Aggiorna reso" : "Registra reso"}
          </button>
          {editId && <button type="button" data-testid="reso-cancel" onClick={cancelEdit} className="text-sm text-slate-400 hover:text-slate-200 flex items-center gap-1"><X className="w-3 h-3" /> Annulla</button>}
        </form>
      )}

      <div className="space-y-3" data-testid="resi-list">
        {items.length === 0 && <p className="text-slate-500 text-sm">Nessun reso registrato.</p>}
        {items.map((r) => (
          <div key={r.id} data-testid={`reso-${r.id}`} className="bg-slate-900/50 border border-slate-800 rounded-md p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-md bg-orange-500/15 flex items-center justify-center shrink-0"><Undo2 className="w-5 h-5 text-orange-300" /></div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-slate-100 flex items-center gap-1"><User className="w-3.5 h-3.5 text-slate-400" />{r.cliente}</span>
                  {r.prodotto && <span className="text-sm text-slate-300">· {r.prodotto}</span>}
                  <span className="text-[11px] text-slate-500">{new Date(r.data || r.created_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
                </div>
                {r.note && <p className="text-sm text-slate-400 mt-1">{r.note}</p>}
                <p className="text-[11px] text-slate-500 mt-1">Autista: {r.created_by_name || "—"}</p>
                {r.foto_file_ids?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {r.foto_file_ids.map((id) => (
                      <a key={id} href={fileUrl(id)} target="_blank" rel="noreferrer">
                        <img src={fileUrl(id)} alt="reso" className="w-16 h-16 rounded-md object-cover border border-slate-700 hover:border-primary transition-colors" />
                      </a>
                    ))}
                  </div>
                )}
              </div>
              {canManage && (
                <div className="flex items-center gap-1 shrink-0">
                  <button data-testid={`edit-reso-${r.id}`} onClick={() => startEdit(r)} className="p-2 text-slate-500 hover:text-primary transition-colors"><Pencil className="w-4 h-4" /></button>
                  <button data-testid={`delete-reso-${r.id}`} onClick={() => remove(r.id)} className="p-2 text-slate-500 hover:text-red-400 transition-colors"><Trash2 className="w-4 h-4" /></button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
